import TariffService from "@/services/tariff.service";
import { Module } from "vuex";
import { SubscriptionResponse } from "@/struct/SubscriptionDataStruct";
import {
  ChangeTariffPayload,
  TariffArray,
  TariffOptionStruct,
} from "@/struct/TariffStruct";
import { convertKBToGB, formatDate } from "@/utils/format";
import { formatDiscountName, getPricePeriodKey } from "@/utils/tariff";
import type { RootState } from "@/store";

const tariffService = new TariffService();

export interface TariffState {
  tariffArray: TariffArray[];
  availableTariffs: TariffArray[];
  subscriptionDetails: SubscriptionResponse;
  isLoadingSubscriptionDetails: boolean;
}

const getUnits = (id: number) => {
  switch (id) {
    case 1:
      return "min";
    case 2:
      return "sms";
    case 3:
      return "gb";
    default:
      break;
  }
};

/** True only when the backend actually sent a usable numeric price. */
const hasAmount = (value: unknown): boolean =>
  value !== null &&
  value !== undefined &&
  value !== "" &&
  Number.isFinite(Number(value));

/** Prices arrive as strings ("3000"); components expect numbers. */
const toAmount = (value: unknown): number =>
  hasAmount(value) ? Number(value) : 0;

const defaultOptions: TariffOptionStruct[] = [
  { value: 1, maximum: 1, id: "gb" },
  { value: 0, maximum: 1, id: "min" },
  { value: 0, maximum: 1, id: "sms" },
];

const getDefaultState = () => ({
  tariffArray: [] as TariffArray[],
  isLoadingSubscriptionDetails: false,
  subscriptionDetails: {} as SubscriptionResponse,
  availableTariffs: [] as TariffArray[],
});

const tariffModule: Module<TariffState, RootState> = {
  namespaced: true,
  state: getDefaultState(),
  getters: {
    currentUserPhone: (state) =>
      state.subscriptionDetails?.mainProduct?.msisdnId || 0,

    walletAmount: (state) => state.subscriptionDetails?.wallet || "0",
    walletAmountCalc: (state) => {
      const walletAmount = state.subscriptionDetails?.wallet || "0";
      const value = Math.floor(parseInt(walletAmount));
      return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, " ");
    },
    addonOptions(state) {
      if (!state.subscriptionDetails?.additionalProducts) return [];
      return state.subscriptionDetails?.additionalProducts.map((item) => {
        const baseAddon = {
          id: item.productId,
          title: item.productName,
          date: formatDate(item.expiresOn),
          amount: item.activationPrice,
        };
        if (!item.unlimited) {
          const isGb = item.counterTypeId === 3;
          const isMin = item.counterTypeId === 1;
          return {
            ...baseAddon,
            unitData: {
              maximum: isGb
                ? convertKBToGB(item.amount)
                : isMin
                ? Math.floor(item.amount / 60)
                : item.amount,
              value: isGb
                ? convertKBToGB(item.liveAmount)
                : isMin
                ? Math.floor(item.liveAmount / 60)
                : item.liveAmount,
              id: getUnits(item.counterTypeId),
            },
          };
        } else return baseAddon;
      });
    },

    mainTariff(state) {
      if (!state.subscriptionDetails?.mainProduct) return null;

      const main = state.subscriptionDetails?.mainProduct;

      const tariffOptions = [
        {
          id: "min",
          value: Math.floor(main.voiceData.liveAmount / 60),
          maximum: Math.floor(main.voiceData.amount / 60),
        },
        {
          id: "sms",
          value: main.smsData.liveAmount,
          maximum: main.smsData.amount,
        },
        {
          id: "gb",
          value: convertKBToGB(main.internetData.liveAmount),
          maximum: convertKBToGB(main.internetData.amount),
        },
      ];

      // Полная цена за цикл, до промо-скидки.
      const fullPrice = hasAmount(main.mainProductFullPrice)
        ? toAmount(main.mainProductFullPrice)
        : toAmount(main.mainProductActivationPrice);

      // Сумма, которая реально спишется в следующем цикле: бэкенд уже
      // применил renewalDiscount. НЕ считаем скидку на клиенте.
      const renewalPrice = hasAmount(main.mainProductRenewalPrice)
        ? toAmount(main.mainProductRenewalPrice)
        : fullPrice;

      // renewalDiscount — это ПРОЦЕНТ (100 === 100%), а не рубли.
      const discountPercent = toAmount(main.promotion?.renewalDiscount);
      const isOfferAnnual = main.isOfferAnnual ?? false;

      // Годовые офферы показываем полной ценой: ни зачёркнутой цены,
      // ни подписи "Скидка N%". Обнуляем скидку в одном месте, поэтому
      // ниже по стеку (discountPercent / hasDiscount / discountName)
      // ничего проверять не нужно.
      const discountInfo =
        !isOfferAnnual && main.promotion && discountPercent > 0
          ? {
              percent: discountPercent,
              name: formatDiscountName(
                main.promotion.displayName || main.promotion.name
              ),
            }
          : null;

      return {
        title: main.mainProductName,
        options: tariffOptions,
        nextDate: formatDate(main.renewalDate),
        amount: fullPrice,
        fullPrice,
        renewalPrice,
        activationPrice: toAmount(main.mainProductActivationPrice),
        facility: main.facilityId,
        subscriptionId: main.subscriptionId,
        compatibleId: main.mainProductId,
        service: main.service,
        isOfferAnnual,
        // Подпись к цене: "за месяц" / "за квартал" / "за полгода" / "за год"
        pricePeriodKey: getPricePeriodKey(
          isOfferAnnual,
          main.promotion?.validityPeriod,
          main.duration
        ),
        discount: discountInfo,
      };
    },
    displayOptions(state, getters) {
      return getters.mainTariff?.options?.length
        ? getters.mainTariff?.options
        : defaultOptions;
    },
    /**
     * Цена следующего списания (со скидкой, если она есть).
     * Заменяет прежний `tariffPriceDiscount`, который отдавал цену
     * АКТИВАЦИИ и поэтому игнорировал renewalDiscount.
     */
    tariffRenewalPrice(state, getters) {
      return getters.mainTariff?.renewalPrice ?? 0;
    },
    /** Полная цена за цикл, до промо-скидки. */
    tariffFullPrice(state, getters) {
      return getters.mainTariff?.fullPrice ?? 0;
    },
  },
  mutations: {
    SET_SUBSCRIPTION_DETAILS(state, value: SubscriptionResponse) {
      state.subscriptionDetails = value;
    },
    SET_IS_LOADING_SUBSCRIPTION_DETAILS(state, isLoading: boolean) {
      state.isLoadingSubscriptionDetails = isLoading;
    },
    SET_AVAILABLE_TARIFFS(state, value: TariffArray[]) {
      state.availableTariffs = value;
    },
  },
  actions: {
    updateSubscriptionDetails({ commit, dispatch }) {
      commit("SET_IS_LOADING_SUBSCRIPTION_DETAILS", true);
      return tariffService
        .getSubscriptionDetails()
        .then((res) => {
          commit("SET_SUBSCRIPTION_DETAILS", res.data.response);
          const operator = res.data?.response?.mainProduct?.operator;
          dispatch("brand/syncOperatorBrand", operator, { root: true });
          return res;
        })
        .catch(() => {
          console.log("Error Update Subscription Details");
          dispatch(
            "system/openModal",
            { presetKey: "general_error" },
            { root: true }
          );
        })
        .finally(() => {
          commit("SET_IS_LOADING_SUBSCRIPTION_DETAILS", false);
        });
    },

    changeTariff(_ctx, payload: ChangeTariffPayload) {
      return tariffService.changeTariff(payload);
    },
    /**
     * BasePromocode ждёт исходный промис axios: по err.response.data.code
     * он различает "не найден" / "нельзя применить" / "уже использован".
     */
    applyPromo(_ctx, code: string) {
      return tariffService.applyPromo(code);
    },
    updateOffers({ commit, getters }) {
      return tariffService
        .getOffers()
        .then((res) => {
          const offers = res?.data?.data;
          if (!Array.isArray(offers)) {
            console.error("Получен некорректный формат офферов");
            return false;
          }
          const currentCompatibleId = getters.mainTariff?.compatibleId;
          const filteredTariffs = offers.filter(
            (item) => item.ems_product_id !== currentCompatibleId
          );
          commit("SET_AVAILABLE_TARIFFS", filteredTariffs);
          return true;
        })
        .catch((err) => {
          console.error("Системная ошибка при обновлении офферов:", err);
          return false;
        });
    },
    clearTariffStore({ state }) {
      Object.assign(state, getDefaultState());
    },
  },
};

export default tariffModule;
